import { cache } from "react"
import { i18nConfig } from "@/i18Config"
import type { IContentItem } from "@kontent-ai/delivery-sdk"

import type { HighlightedContent } from "@/types/kontent-generated/types/highlighted-content.generated"
import { IKTRichText } from "@/types/kontent.types"
import { getDeliveryClient } from "@/lib/kontent/client"

import { assertItemResponse, fetchKontent } from "../helpers"
import {
  mapRichTextElementToIKTRichText,
  parseCustomElementJsonValue,
} from "../mappers"
import { resolveRichText } from "../richText"

export type TResolvedHighlightedContent = {
  title: string
  icon: string
  content: IKTRichText
}

/**
 * Maps an already-fetched SDK item, avoiding a redundant Delivery API call
 * when the item was already resolved by a parent query's depth parameter.
 */
export async function GetHighlightedContentFromItem(
  rawItem: IContentItem,
  previewMode = false
): Promise<TResolvedHighlightedContent> {
  const item = rawItem as HighlightedContent

  let content: IKTRichText = mapRichTextElementToIKTRichText(
    item.elements.content
  )
  content = await resolveRichText(content, previewMode)

  return {
    title: item.elements.title.value,
    icon: parseCustomElementJsonValue(item.elements.icon_selector.value) ?? "",
    content,
  }
}

async function _fetchHighlightedContent(
  codename: string,
  previewMode = false
): Promise<TResolvedHighlightedContent> {
  return fetchKontent("HighlightedContent", async () => {
    const client = getDeliveryClient(previewMode)
    const response = await client
      .item<HighlightedContent>(codename)
      .languageParameter(i18nConfig.defaultLocale)
      .depthParameter(2)
      .toPromise()

    const item = assertItemResponse(response, "HighlightedContent")
    return GetHighlightedContentFromItem(item, previewMode)
  })
}

export const GetHighlightedContent = cache(_fetchHighlightedContent)
